import type { MeasurementPanel, PanelData } from "./api";

export type DashboardResolution = "auto" | 60 | 900 | 3600 | 86400;

export type ChartPoint = {
  timestamp: number;
  value: number | null;
  sum?: number;
  count?: number;
};

export type ChartSeries = {
  key: string;
  groups: Record<string, unknown>;
  points: ChartPoint[];
};

export type NormalizedChartData = {
  rangeStart: number;
  rangeEnd: number;
  bucketSeconds: number;
  series: ChartSeries[];
  leadValue: number | null;
  leadTimestamp: number | null;
};

const supportedBuckets = new Set([60, 900, 3600, 86400]);

function stableGroups(groups: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(groups).sort(([left], [right]) => left.localeCompare(right)));
}

function groupKey(groups: Record<string, unknown>) {
  return JSON.stringify(stableGroups(groups));
}

function finiteTimestamp(value: string | undefined) {
  const timestamp = value ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : Number.NaN;
}

function responseRange(data: PanelData) {
  const points = data.points.map((point) => finiteTimestamp(point.ts)).filter(Number.isFinite);
  const requestedStart = finiteTimestamp(data.range_start);
  const requestedEnd = finiteTimestamp(data.range_end);
  const rangeStart = Number.isFinite(requestedStart) ? requestedStart : Math.min(...points);
  const rangeEnd = Number.isFinite(requestedEnd) && requestedEnd > rangeStart ? requestedEnd : Math.max(...points) + 1;
  return { rangeStart, rangeEnd };
}

function normalizedBucketSeconds(value: number | undefined, fallback: number | undefined) {
  if (value && supportedBuckets.has(value)) return value;
  if (fallback && supportedBuckets.has(fallback)) return fallback;
  return 900;
}

function timeGrid(rangeStart: number, rangeEnd: number, bucketSeconds: number) {
  const bucketMilliseconds = bucketSeconds * 1000;
  const result: number[] = [];
  for (let timestamp = rangeStart; timestamp < rangeEnd; timestamp += bucketMilliseconds) result.push(timestamp);
  return result;
}

function isZeroFilledAggregation(aggregation: MeasurementPanel["aggregation"]) {
  return aggregation === "sum" || aggregation === "count";
}

/**
 * Makes the response's authoritative range usable by every renderer. Missing
 * count/sum buckets become zero, while measurement aggregations remain gaps.
 */
export function normalizePanelChartData(data: PanelData, panel: MeasurementPanel, knownClassValues: string[] = []): NormalizedChartData | null {
  const { rangeStart, rangeEnd } = responseRange(data);
  if (!Number.isFinite(rangeStart) || !Number.isFinite(rangeEnd) || rangeEnd <= rangeStart) return null;
  const bucketSeconds = normalizedBucketSeconds(data.bucket_seconds, panel.grain_seconds);
  const timestamps = timeGrid(rangeStart, rangeEnd, bucketSeconds);
  const valuesBySeries = new Map<string, { groups: Record<string, unknown>; values: Map<number, number>; weights: Map<number, { sum: number; count: number }> }>();
  const observedTimestamps = new Set<number>();

  function ensureSeries(groups: Record<string, unknown>) {
    const normalizedGroups = stableGroups(groups);
    const key = groupKey(normalizedGroups);
    let series = valuesBySeries.get(key);
    if (!series) {
      series = { groups: normalizedGroups, values: new Map(), weights: new Map() };
      valuesBySeries.set(key, series);
    }
    return series;
  }

  if (knownClassValues.length === 0 && (panel.group_by.length === 0 || data.points.length === 0)) ensureSeries({});
  for (const vehicleClass of knownClassValues) ensureSeries({ class: vehicleClass });
  for (const point of data.points) {
    const timestamp = finiteTimestamp(point.ts);
    if (!Number.isFinite(timestamp) || timestamp < rangeStart || timestamp >= rangeEnd || !Number.isFinite(point.value)) continue;
    const series = ensureSeries(point.groups);
    series.values.set(timestamp, (series.values.get(timestamp) ?? 0) + point.value);
    if (Number.isFinite(point.sum) && Number.isFinite(point.count) && point.count! > 0) {
      const prior = series.weights.get(timestamp);
      const weight = { sum: (prior?.sum ?? 0) + point.sum!, count: (prior?.count ?? 0) + point.count! };
      series.weights.set(timestamp, weight);
      if (panel.aggregation === "avg") series.values.set(timestamp, weight.sum / weight.count);
    }
    observedTimestamps.add(timestamp);
  }

  const zeroFill = isZeroFilledAggregation(panel.aggregation);
  const series = Array.from(valuesBySeries.entries()).map(([key, value]) => ({
    key,
    groups: value.groups,
    points: timestamps.map((timestamp) => ({ timestamp, value: value.values.get(timestamp) ?? (zeroFill ? 0 : null), ...value.weights.get(timestamp) })),
  }));
  const leadTimestamp = observedTimestamps.size > 0 ? Math.max(...observedTimestamps) : timestamps.at(-1) ?? null;
  const leadPointIndex = leadTimestamp === null ? -1 : timestamps.indexOf(leadTimestamp);
  const leadValues = leadPointIndex < 0 ? [] : series.map((item) => item.points[leadPointIndex]?.value).filter((value): value is number => value !== null && value !== undefined);
  const leadValue = leadValues.length === 0 ? null
    : zeroFill ? leadValues.reduce((total, value) => total + value, 0)
      : leadValues.at(-1) ?? null;
  return { rangeStart, rangeEnd, bucketSeconds, series, leadValue, leadTimestamp };
}

/**
 * Reduces every non-gap value in the requested range to one KPI value. The
 * bucket size controls the source resolution, but never limits a KPI to the
 * latest bucket.
 */
export function aggregatePanelChartValue(chart: NormalizedChartData, aggregation: MeasurementPanel["aggregation"]) {
  let count = 0;
  let sum = 0;
  let minimum = Number.POSITIVE_INFINITY;
  let maximum = Number.NEGATIVE_INFINITY;
  for (const series of chart.series) {
    for (const point of series.points) {
      if (point.value === null) continue;
      count += aggregation === "avg" ? point.count ?? 1 : 1;
      sum += aggregation === "avg" ? point.sum ?? point.value : point.value;
      minimum = Math.min(minimum, point.value);
      maximum = Math.max(maximum, point.value);
    }
  }
  if (count === 0) return null;
  if (aggregation === "sum" || aggregation === "count") return sum;
  if (aggregation === "min") return minimum;
  if (aggregation === "max") return maximum;
  if (aggregation === "avg") return sum / count;
  return chart.leadValue;
}

export function panelDataWithRequestContext(data: PanelData, from: Date, to: Date, resolution: DashboardResolution): PanelData {
  const requestedBucket = resolution === "auto" ? undefined : resolution;
  return {
    ...data,
    timezone: data.timezone ?? "Europe/Vienna",
    bucket_seconds: data.bucket_seconds ?? requestedBucket,
    range_start: data.range_start ?? from.toISOString(),
    range_end: data.range_end ?? to.toISOString(),
  };
}
